/**
 * The whole editor: a palette, a canvas, and a bar for what is selected.
 *
 * The canvas stays a surface that knows nothing about panels, and this
 * composes the three. Undo lives HERE rather than inside the canvas,
 * because a chart and the runsheet beside it have to move together — a
 * canvas that owned its own history could be undone out of step with
 * everything else on the page, which is how a chart and a table end up
 * disagreeing about who owns land.
 *
 * History is snapshots, not inverse operations. The day somebody adds a
 * way to change the chart and forgets to write its inverse, an
 * operation-based undo starts quietly corrupting the diagram. Comparing
 * whole documents cannot be forgotten.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import DiagramCanvas from './DiagramCanvas.jsx';
import ShapeLibrary from './ShapeLibrary.jsx';
import StyleBar from './StyleBar.jsx';
import { addShape, copyItems, pasteItems } from '../../lib/canvas/document.js';
import { shapeFrom } from '../../lib/canvas/shapes.js';
import { clampFont, stepFont } from '../../lib/canvas/fonts.js';
import { updateEdges, updateShapes } from '../../lib/canvas/document.js';
import {
  chartBounds, chartFilename, pdfFrom, pngFrom, rasterise, save, scaleFor, svgFromSurface,
} from '../../lib/canvas/exportChart.js';
import { checkChain } from '../../lib/canvas/chainCheck.js';
import ChainCheck from './ChainCheck.jsx';
import './DiagramEditor.css';

// Deep enough to walk out of a bad run of edits, shallow enough that the
// steps — whole copies of the diagram — stay small beside everything
// else a page holds.
const HISTORY = 60;

/**
 * How hard to push each export, and how big a canvas it may ask for.
 *
 * A PNG goes on a screen or into a report, so twice the drawing is
 * plenty and the ceiling is only there to stop a browser refusing an
 * enormous canvas — which it does by handing back a blank one rather
 * than by failing.
 *
 * A PDF is the thing that gets printed, so it is pushed to three times
 * (216 dots to the inch, since a world unit becomes a point) under a
 * tighter ceiling: those pixels are deflated one by one on the way into
 * the file, and past about twelve million the tab stops answering long
 * enough to look broken.
 */
const EXPORTS = {
  png: { scale: 2, ceiling: 40e6 },
  pdf: { scale: 3, ceiling: 12e6 },
};

export default function DiagramEditor({
  doc,
  onChange,
  readOnly = false,
  showLibrary = true,
  toolbar = null,
  onOpenDocument = null,
  // What the exported file is called. A folder of `chain-of-title.png`
  // is a folder nobody can use.
  title = '',
  // Everybody else in this chain right now, and where to tell them the
  // pointer is. Given by whatever owns the connection, so the editor
  // stays a thing that can be dropped on a page with no network at all.
  peers = null,
  onPointerReport = null,
  who = '',
  // Passed straight through to the canvas: the signal that a chart has
  // just been loaded and should be fitted on screen once. The editor
  // has no opinion about it, it just owns the canvas.
  fitKey = null,
}) {
  const [selection, setSelection] = useState([]);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [pending, setPending] = useState(null);
  const [exporting, setExporting] = useState(null);
  // The findings, held from when Check was pressed rather than
  // recomputed as the chart changes. A list that rewrote itself under
  // the pointer while somebody was working down it would be unusable —
  // and fixing one finding is exactly what changes the chart.
  const [checked, setChecked] = useState(null);
  const [openFinding, setOpenFinding] = useState(null);
  const view = useRef(null);
  // The editor's own bounds, so its shortcuts can tell whether this is
  // the chart being worked in. See the note on the key handler below.
  const root = useRef(null);

  /**
   * Take a step, then apply the change.
   *
   * The step is the document as it stood BEFORE, captured at commit time
   * rather than on every keystroke of a drag — otherwise one drag across
   * a screen is two hundred undo steps and Back stops meaning anything.
   */
  const commit = useCallback((label) => {
    setPast((steps) => [...steps, { doc: pending ?? doc, label }].slice(-HISTORY));
    // A new change ends the old forward path, as in any editor.
    setFuture([]);
    setPending(null);
  }, [doc, pending]);

  // Held from the first change of a gesture so the step is the state
  // before the gesture, not before its last pixel.
  const change = useCallback((next) => {
    setPending((held) => (held === null ? doc : held));
    onChange(next);
  }, [doc, onChange]);

  /**
   * Back and forward.
   *
   * The stacks are read from state rather than from inside an updater.
   * React runs an updater DURING RENDER — sometimes twice — so calling
   * onChange or setFuture in there updates another component mid-render,
   * and the history ends up applied once, twice, or not at all depending
   * on how the render went. It is the kind of bug that looks like
   * "undo is flaky" and never reproduces on demand.
   */
  const undo = useCallback(() => {
    if (!past.length) return;
    const step = past[past.length - 1];
    setPast(past.slice(0, -1));
    setFuture([{ doc, label: step.label }, ...future]);
    setPending(null);
    onChange(step.doc);
  }, [past, future, doc, onChange]);

  const redo = useCallback(() => {
    if (!future.length) return;
    const step = future[0];
    setFuture(future.slice(1));
    setPast([...past, { doc, label: step.label }]);
    setPending(null);
    onChange(step.doc);
  }, [past, future, doc, onChange]);

  const place = useCallback((item, at) => {
    const spot = at || view.current?.centre() || { x: 0, y: 0 };
    const out = addShape(doc, shapeFrom(item, spot));
    setPast([...past, { doc, label: 'Add' }].slice(-HISTORY));
    setFuture([]);
    setPending(null);
    onChange(out.doc);
    setSelection([out.shape.id]);
  }, [doc, past, onChange]);

  const dragging = useRef(null);
  // The clipboard is the app's own, not the system's. Reading the system
  // clipboard needs a permission prompt and gives back text, and what is
  // being copied here is shapes with their connectors — so a private one
  // is both simpler and the only thing that can round-trip. A ref, not
  // state: nothing on screen shows what is in it.
  const clip = useRef(null);

  const copy = useCallback(() => {
    if (!selection.length) return false;
    clip.current = copyItems(doc, selection);
    return true;
  }, [doc, selection]);

  const paste = useCallback((at = null) => {
    if (!clip.current?.shapes?.length) return;
    // Where the pointer is, which is where a paste is expected to land.
    // Read off the canvas rather than passed in, because the keyboard
    // event that triggers it carries no position of its own.
    const spot = at || view.current?.pointer || null;
    const out = pasteItems(doc, clip.current, { at: spot });
    setPast([...past, { doc, label: 'Paste' }].slice(-HISTORY));
    setFuture([]);
    setPending(null);
    onChange(out.doc);
    setSelection(out.ids);
  }, [doc, past, onChange]);

  const cut = useCallback(() => {
    if (!copy()) return;
    const out = { ...doc, shapes: doc.shapes.filter((s) => !selection.includes(s.id)) };
    const alive = new Set(out.shapes.map((s) => s.id));
    out.edges = doc.edges.filter((e) => (
      !selection.includes(e.id)
      && (!e.from?.shapeId || alive.has(e.from.shapeId))
      && (!e.to?.shapeId || alive.has(e.to.shapeId))
    ));
    setPast([...past, { doc, label: 'Cut' }].slice(-HISTORY));
    setFuture([]);
    setPending(null);
    onChange(out);
    setSelection([]);
  }, [copy, doc, past, selection, onChange]);

  /** Grow or shrink the text on whatever is selected. */
  const resize = useCallback((direction) => {
    if (!selection.length) return;
    const bump = (item) => ({ fontSize: clampFont(stepFont(item.fontSize, direction)) });
    let next = updateShapes(doc, selection, bump);
    next = updateEdges(next, selection, bump);
    setPast([...past, { doc, label: 'Font size' }].slice(-HISTORY));
    setFuture([]);
    onChange(next);
  }, [doc, past, selection, onChange]);

  const runCheck = useCallback(() => {
    if (checked) { setChecked(null); setOpenFinding(null); return; }
    setChecked(checkChain(doc));
    setOpenFinding(null);
  }, [checked, doc]);

  /** Show what a finding is about, on the chart. */
  const showFinding = useCallback((index, finding) => {
    setOpenFinding(index);
    const ids = finding.ids || [];
    setSelection(ids);
    view.current?.look?.(ids);
  }, []);

  /**
   * Hand the chain over as a file.
   *
   * The background is read off the canvas rather than assumed white: the
   * chart lives on a dark page inside this app and on a light one in the
   * export, and the label cards behind the deed text have to match
   * whatever the paper actually is or they show as patches.
   */
  const download = useCallback(async (kind) => {
    const surface = view.current?.surface?.();
    if (!surface) return;
    if (!doc.shapes.length && !doc.edges.length) {
      setExporting({ error: 'There is nothing on the canvas to export.' });
      return;
    }
    setExporting({ busy: kind });
    try {
      const host = surface.parentElement;
      const paper = host
        ? getComputedStyle(host).getPropertyValue('--dgm-paper').trim() || '#ffffff'
        : '#ffffff';
      const bounds = chartBounds(doc.shapes, view.current.routes);
      const want = EXPORTS[kind];
      const scale = scaleFor(bounds, want.scale, want.ceiling);
      const svg = svgFromSurface(surface, { bounds, background: paper });
      const canvas = await rasterise(svg, bounds, scale);
      const blob = kind === 'pdf' ? pdfFrom(canvas, scale) : await pngFrom(canvas);
      save(blob, chartFilename(title, kind));
      setExporting(null);
    } catch (err) {
      setExporting({ error: err?.message || 'The chart could not be exported.' });
    }
  }, [doc, title]);

  /**
   * The shortcuts.
   *
   * On WINDOW, not on the div. A div is not focusable, so an onKeyDown
   * on it fires only when something inside it has focus — which for a
   * canvas somebody has just clicked is nothing. These never ran, and
   * the reason they looked like they did is that undo and redo also have
   * buttons.
   */
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      // Never steal a shortcut from a field somebody is typing in —
      // including the text editor over a shape, which is a textarea.
      if (/^(INPUT|TEXTAREA)$/.test(e.target.tagName) || e.target.isContentEditable) return;
      // And never from another chart, or from a document opened over this
      // one. Window is the only place these can listen — a div is not
      // focusable and an onKeyDown on one never fires — so the test for
      // "is this mine" is where the focus actually is. Two charts on one
      // page otherwise both undid, and ⌘V pasted into both.
      if (!root.current?.contains(document.activeElement)) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); redo(); }
      else if (key === 'c') { if (copy()) e.preventDefault(); }
      else if (key === 'x' && !readOnly) { e.preventDefault(); cut(); }
      else if (key === 'v' && !readOnly) { e.preventDefault(); paste(null); }
      // The habit from every word processor: ⌘⇧. and ⌘⇧, step the size.
      else if (e.shiftKey && (e.key === '>' || e.key === '.')) { e.preventDefault(); resize(1); }
      else if (e.shiftKey && (e.key === '<' || e.key === ',')) { e.preventDefault(); resize(-1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, copy, cut, paste, resize, readOnly]);

  return (
    <div className="dge" ref={root}>
      {showLibrary && !readOnly && (
        <ShapeLibrary
          onPlace={(item) => place(item, null)}
          onDragStart={(item) => { dragging.current = item; }}
        />
      )}

      <div className="dge-main">
        <div className="dge-bar">
          <button type="button" onClick={undo} disabled={!past.length} title="Undo (⌘Z)">
            ↶ Undo
          </button>
          <button type="button" onClick={redo} disabled={!future.length} title="Redo (⌘⇧Z)">
            ↷ Redo
          </button>
          <span className="dge-sep" />
          <button
            type="button" onClick={runCheck} className={checked ? 'is-on' : undefined}
            title="Read the chain back and say what is wrong with it"
            aria-pressed={Boolean(checked)}
          >
            ✓ Check chain
          </button>
          <button
            type="button" onClick={() => download('png')} disabled={Boolean(exporting?.busy)}
            title="Save the whole chain as an image"
          >
            {exporting?.busy === 'png' ? 'Saving…' : '⤓ PNG'}
          </button>
          <button
            type="button" onClick={() => download('pdf')} disabled={Boolean(exporting?.busy)}
            title="Save the whole chain as a PDF to print or send"
          >
            {exporting?.busy === 'pdf' ? 'Saving…' : '⤓ PDF'}
          </button>
          {toolbar}
          {exporting?.error && <span className="dge-status is-bad">{exporting.error}</span>}
          {/* Said in words as well as drawn, because somebody whose
              pointer is off the canvas has no cursor to see — and
              "Ada is here" is the part that changes how you work. */}
          {who && <span className="dge-who" title={`${who} — also in this chain`}>{who}</span>}
          <span className="dge-count">
            {doc.shapes.length} shape{doc.shapes.length === 1 ? '' : 's'}
            {' · '}
            {doc.edges.length} connector{doc.edges.length === 1 ? '' : 's'}
          </span>
        </div>

        {/* Above the canvas rather than floating over the selection: a
            bar that moves with what you picked covers the thing you
            picked, which on a dense chain is the neighbours you were
            comparing it against. */}
        {!readOnly && (
          <StyleBar doc={doc} selection={selection} onChange={change} onCommit={commit} />
        )}

        <div className="dge-canvas">
          <DiagramCanvas
            doc={doc}
            onChange={change}
            onCommit={commit}
            selection={selection}
            onSelectionChange={setSelection}
            readOnly={readOnly}
            viewRef={view}
            onOpenDocument={onOpenDocument}
            ariaLabel={title ? `Chain of title — ${title}` : 'Chain of title'}
            peers={peers}
            onPointerReport={onPointerReport}
            fitKey={fitKey}
            flagged={openFinding !== null && checked?.[openFinding]
              ? checked[openFinding].ids
              : undefined}
            onDropShape={(at) => {
              if (!dragging.current) return;
              place(dragging.current, at);
              dragging.current = null;
            }}
          />
        </div>
      </div>

      {checked && (
        <ChainCheck
          findings={checked}
          chosen={openFinding}
          onChoose={showFinding}
          onClose={() => { setChecked(null); setOpenFinding(null); }}
        />
      )}
    </div>
  );
}
